import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

// Returns the YYYY-MM-DD portion of any parseable date value (ISO string, ms epoch,
// or Date), or undefined when blank/unparseable. Unlike `new Date(x).toISOString()`,
// it never throws on bad input.
export function toIsoDate(value: string | number | Date | null | undefined): string | undefined {
  if (value === null || value === undefined || value === "") return undefined;
  const ts =
    typeof value === "number" ? value : value instanceof Date ? value.getTime() : Date.parse(value);
  if (Number.isNaN(ts)) return undefined;
  return new Date(ts).toISOString().slice(0, 10);
}

export function getLocalTimeFromDateTime(
  utcTime: string | Date,
  includeSeconds: boolean = true,
  includeAmPm: boolean = true
): string {
  let formatString = includeSeconds ? "HH:mm:ss" : "HH:mm";
  if (includeAmPm) {
    formatString += " A";
  }
  return dayjs.utc(utcTime).local().format(formatString);
}

/**
 * Normalizes a timestamp to milliseconds, handling both second and millisecond precision.
 * Unix timestamps in seconds are 10 digits, milliseconds are 13 digits.
 * Accepts both number and string inputs for real-world API compatibility.
 * 
 * @param timestamp - Unix timestamp in seconds or milliseconds (number or string)
 * @returns Normalized timestamp in milliseconds
 */
// 10-digit seconds → below 1 e11.  Milliseconds already ≥ 1 e11 (1973-03-03).
export function normalizeTimestamp(timestamp: number | string): number {
  // Convert string input to number
  const numericTimestamp =
    typeof timestamp === 'string' ? Number(timestamp) : timestamp;

  // Reject non-finite or non-positive input
  if (!Number.isFinite(numericTimestamp) || numericTimestamp <= 0) {
    return 0;
  }

  const SECONDS_THRESHOLD = 100_000_000_000; // 1e11
  return numericTimestamp < SECONDS_THRESHOLD ? numericTimestamp * 1000 : numericTimestamp;
}
